import { motion, useReducedMotion } from "framer-motion";
import { readAlpsPillars } from "../constants/alps";
import styles from "../styles/AlpsPath.module.scss";

const EASE = [0.22, 1, 0.36, 1];

/*
  One coherent cascade rather than four independent reveals: the list owns the
  stagger, so the letters always arrive in order — A, then its connector drawn
  downward, then L, and so on — however the block enters the viewport. The
  pacing is deliberately unhurried; the point of the animation is to show that
  ALPS is a sequence of four steps, and that is only legible if the eye can
  follow it.
*/
const listMotion = {
  hidden: {},
  visible: { transition: { delayChildren: 0.15, staggerChildren: 0.42 } },
};

const itemMotion = {
  hidden: {},
  visible: {},
};

const markerMotion = {
  hidden: { opacity: 0, scale: 0.7 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: EASE } },
};

const wordMotion = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, delay: 0.14, ease: EASE },
  },
};

/* Drawn downward from the node above, so the connector reads as "then". */
const linkMotion = {
  hidden: { scaleY: 0 },
  visible: {
    scaleY: 1,
    transition: { duration: 0.5, delay: 0.34, ease: EASE },
  },
};

/**
 * The ALPS brand signature as a path: four letter nodes stacked vertically and
 * linked by short connectors, Ascolta → Leggi → Parla → Scrivi. Each node
 * carries its own brand tone, running cool to bright down the four steps.
 * Words are read from the approved copy, so nothing is invented here.
 */
const AlpsPath = ({ tone = "dark" }) => {
  const reduceMotion = useReducedMotion();
  const pillars = readAlpsPillars();

  if (pillars.length === 0) return null;

  const animated = !reduceMotion;

  return (
    <div className={`${styles.path} ${tone === "dark" ? styles.onDark : ""}`}>
      <motion.ol
        className={styles.list}
        variants={animated ? listMotion : undefined}
        initial={animated ? "hidden" : false}
        whileInView={animated ? "visible" : undefined}
        viewport={{ once: true, amount: 0.35 }}
      >
        {pillars.map((word, index) => (
          <motion.li
            key={word}
            className={styles.node}
            data-tone={index % 4}
            variants={animated ? itemMotion : undefined}
          >
            <motion.span
              className={styles.marker}
              aria-hidden="true"
              variants={animated ? markerMotion : undefined}
            >
              <span className={styles.dot}>{word.charAt(0)}</span>
            </motion.span>

            {index < pillars.length - 1 && (
              <motion.span
                className={styles.link}
                aria-hidden="true"
                variants={animated ? linkMotion : undefined}
              />
            )}

            <motion.span
              className={styles.word}
              variants={animated ? wordMotion : undefined}
            >
              {word}
            </motion.span>
          </motion.li>
        ))}
      </motion.ol>
    </div>
  );
};

export default AlpsPath;
